import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getServiceId } from '@/api/tokens.helper';

import {
  createBerth,
  deleteBerth,
  deleteBerthFile,
  getBerths,
  updateBerth,
  uploadBerthFile,
} from './vessel-marinas.api';
import type { BerthFileSlot, BerthValues } from './vessel-marinas.dto';

export const useBerthsQuery = ({
  enabled = true,
  vesselId = getServiceId(),
}: { enabled?: boolean; vesselId?: string } = {}) =>
  useQuery({
    enabled,
    queryKey: ['berths', vesselId],
    queryFn: () => getBerths(vesselId),
  });

export type SaveBerthInput = {
  /** Absent for a new berth. */
  id?: string;
  values: BerthValues;
  /** A file to put in a slot, or `null` to empty it. Slots left out stay as they are. */
  files?: { slot: BerthFileSlot; file: File | null }[];
  /** A new berth's vessel, when it is not the active one. */
  vesselId?: string;
};

/**
 * Saves the record, then its files. A file that fails does not fail the save: the
 * berth exists by then, and the caller needs its id and the slots still to do, so
 * trying again updates it rather than adding a second one.
 */
export const useSaveBerthMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, values, files = [], vesselId }: SaveBerthInput) => {
      const berthId = id ?? (await createBerth(values, vesselId));
      if (id) await updateBerth(id, values);
      const results = await Promise.allSettled(
        files.map(({ slot, file }) =>
          file ? uploadBerthFile(berthId, slot, file) : deleteBerthFile(berthId, slot),
        ),
      );
      const failedSlots = files.flatMap(({ slot }, i) => {
        const result = results[i];
        if (result.status === 'fulfilled') return [];
        console.error(result.reason);
        return [slot];
      });
      return { id: berthId, failedSlots };
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['berths'] }),
  });
};

export const useDeleteBerthMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteBerth,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['berths'] }),
  });
};
